import React from 'react';
import {
	AbsoluteFill,
	interpolate,
	Sequence,
	useCurrentFrame,
	useVideoConfig,
	Audio
} from 'remotion';
import { z } from 'zod';
import { zColor } from '@remotion/zod-types';
import { CalculateMetadataFunction } from "remotion";
import { ImageCarousel } from "./elements/ImageCarousel";
import { TextCarousel } from "./elements/TextCarousel";
import { LogoBlock } from "./elements/LogoBlock";

import { AnimeJSDemo } from './AnimeJSDemo';

import { loadFont } from "@remotion/fonts";
import { staticFile } from "remotion";

const textItemSchema = z.object({
	content: z.string(),
	style: z.string().optional(),
	colorScheme: z.string().optional(),
	conf: z.nullable(z.object({
		flag: z.boolean().optional(),
		type: z.string().optional(),
		d: z.number().optional(),
	})).optional(),
});

const slideSchema = z.object({
	duration: z.number().optional(),
	lines: z.array(z.string()),
	style: z.string().optional(),
	colorScheme: z.string().optional(),
});

type ApiTextItem = z.infer<typeof textItemSchema>;
type ApiSlide = z.infer<typeof slideSchema>;

const normalizeTextItems = (texts: ApiTextItem[] | undefined): ApiTextItem[] => {
	return (texts ?? []).filter((textItem) => textItem.content.trim().length > 0);
};

const slidesToTextItems = (slides: ApiSlide[] | undefined): ApiTextItem[] => {
	return (slides ?? [])
		.map((slide) => ({
			content: slide.lines.join('\n'),
			style: slide.style,
			colorScheme: slide.colorScheme,
			conf: {
				d: slide.duration ?? 3000,
				type: slide.style === 'terminbox' ? 'terminbox' : undefined,
			},
		}))
		.filter((textItem) => textItem.content.trim().length > 0);
};

const getNormalizedTexts = ({
	texts,
	slides,
}: {
	texts?: ApiTextItem[];
	slides?: ApiSlide[];
}): ApiTextItem[] => {
	const normalizedTexts = normalizeTextItems(texts);
	if (normalizedTexts.length > 0) {
		return normalizedTexts;
	}

	return slidesToTextItems(slides);
};

const resolveRemotionApiUrlFromSlug = (slug: string): string => {
	if (slug.startsWith('http://') || slug.startsWith('https://')) {
		return slug;
	}

	return `https://www.werkzeugh.at/api/remotion/${encodeURIComponent(slug)}`;
};

const apiResponse = z.object({
	config: z.object({
		logoName: z.string().optional(),
	}).optional(),
	title: z.string(),
	description: z.string().optional(),
	images: z.array(z.object({
		url: z.string(),
		image_w: z.number().optional(),
		image_h: z.number().optional(),
		crop_end: z.union([
			z.object({
				x: z.number(),
				y: z.number(),
				width: z.number(),
				height: z.number(),
			}),
			z.object({})
		]).optional().transform((val): { x: number; y: number; width: number; height: number; } | undefined => {
			if (!val || !('x' in val)) return undefined;
			return val as { x: number; y: number; width: number; height: number; };
		}),
		crop_start: z.union([
			z.object({
				x: z.number(),
				y: z.number(),
				width: z.number(),
				height: z.number(),
			}),
			z.object({})
		]).optional().transform((val): { x: number; y: number; width: number; height: number; } | undefined => {
			if (!val || !('x' in val)) return undefined;
			return val as { x: number; y: number; width: number; height: number; };
		}),
		enabled: z.boolean(),
		image_id: z.string(),
		original_width: z.number().optional(),
		original_height: z.number().optional(),
	})),
	videoMdFile: z.string().optional(),
	logoName: z.string().optional(),
	texts: z.array(textItemSchema).optional(),
	slides: z.array(slideSchema).optional(),
}).transform((input) => {
	return {
		...input,
		texts: getNormalizedTexts({
			texts: input.texts,
			slides: input.slides,
		}),
	};
});

type Props = z.infer<typeof talkvideoSchema>;


const fontFamily = "Grift";

loadFont({
	family: fontFamily,
	url: staticFile("grift/Web-TT/Grift-Bold.woff2"),
	weight: "500",
}).then(() => {
	console.log("Font loaded!");
});


export const calcTalkvideoMetadata: CalculateMetadataFunction<Props> = async ({
	props,
}) => {

	console.log('#log props:', props);
	const slug = props.documentSlug || props.videoMdFile || "video1";
	const dataUrl = props.documentUrl
		? props.documentUrl
		: resolveRemotionApiUrlFromSlug(slug);
	const data = await fetch(dataUrl);
	const json = await data.json();

	console.log('#log data:', json);

	// Load SVG content upfront - use config.logoName from API if available
	const apiLogoName = json.config?.logoName;
	const logoFileName = apiLogoName || props.logoName || 'talk_logo2.svg';
	const logoResponse = await fetch(staticFile(`images/logo/${logoFileName}`));
	const logoSvg = await logoResponse.text();

	// Log data before Zod validation
	// console.log('#log before validation - json:', json);
	// console.log('#log before validation - props:', props);
	// console.log('#log before validation - logoSvg length:', logoSvg?.length);

	// Validate the API response
	const validatedData = apiResponse.safeParse(json);

	if (!validatedData.success) {
		console.log('#log validation failed:', validatedData.error);
	}

	const normalizedTexts = validatedData.success
		? validatedData.data.texts
		: getNormalizedTexts({
			texts: json.texts,
			slides: json.slides,
		});

	const totalDurationMs = normalizedTexts.reduce((sum, textItem) => {
		return sum + (textItem.conf?.d || 3000);
	}, 0) || 10000;

	const fps = 30;
	const durationInFrames = Math.ceil((totalDurationMs / 1000) * fps);

	return {
		props: {
			...props,
			data: validatedData.success ? validatedData.data : null,
			logoSvg,
		},
		durationInFrames,
	};
};


export const talkvideoSchema = z.object({
	primaryColor: zColor().default('#4A90E2'),
	secondaryColor: zColor().default('#7DD3FC'),
	accentColor: zColor().default('#F97316'),
	videoMdFile: z.string().optional().default('video1'),
	documentSlug: z.string().optional(),
	documentUrl: z.string().optional(),
	data: z.nullable(apiResponse),
	logoSvg: z.string().optional(),
	logoName: z.string().optional().default('talk_logo2.svg'),
	transitionType: z.enum(['slide', 'fade', 'wipe', 'flip', 'clockWipe', 'iris', 'cube']).optional().default('slide'),
	musicTrack: z.enum(['opener-short', 'opener-long', 'soundtrack-1', 'ukulele', 'none']).optional().default('opener-short'),
	musicVolume: z.number().min(0).max(1).optional().default(1)
});

export const TalkVideo: React.FC<z.infer<typeof talkvideoSchema>> = ({
	primaryColor = '#4A90E2',
	secondaryColor = '#7DD3FC',
	accentColor = '#F97316',
	videoMdFile = 'video1',
	data,
	logoSvg = '',
	logoName = 'talk_logo2.svg',
	transitionType = 'slide',
	musicTrack = 'opener-short',
	musicVolume = 1,
}) => {
	if (data === null) {
		throw new Error("Data was not fetched");
	}
	const frame = useCurrentFrame();
	const { durationInFrames, fps } = useVideoConfig();

	// Fade in/out for entire composition
	const fadeIn = interpolate(frame, [0, 30], [0, 1], {
		extrapolateLeft: 'clamp',
		extrapolateRight: 'clamp',
	});

	// const fadeOut = interpolate(
	// 	frame,
	// 	[durationInFrames - 30, durationInFrames],
	// 	[1, 0],
	// 	{
	// 		extrapolateLeft: 'clamp',
	// 		extrapolateRight: 'clamp',
	// 	}
	// );

	const opacity = fadeIn;

	const imageCarousel = { start: 0, duration: durationInFrames };

	const getMusicSrc = (track: string) => {
		if (track === 'none') return null;

		switch (track) {
			case 'opener-short':
				return staticFile('audio/opener_short.mp3');
			case 'opener-long':
				return staticFile('audio/opener_long.mp3');
			case 'soundtrack-1':
				return staticFile('audio/soundtrack1.mp3');
			case 'ukulele':
				return staticFile('audio/ukulele.mp3');
			default:
				return null;
		}
	};

	// Convert timestamp 00:22:54 to frames
	const audioStartTime = 0;
	//(26 * 60 + 32) * fps; // 22 minutes 54 seconds converted to frames


	return (
		<div>
			<AbsoluteFill style={{ backgroundColor: '#000' }} />

			{/* Background Music */}
			{musicTrack !== 'none' && (() => {
				const audioSrc = getMusicSrc(musicTrack);
				console.log('#log rendering audio:', { musicTrack, audioSrc, musicVolume, audioStartTime });
				return (
					<Audio
						src={audioSrc!}
						volume={musicVolume}
						startFrom={audioStartTime}
					/>
				);
			})()}

			<AbsoluteFill style={{ opacity }}>

				{/* Image Carousel */}
				<Sequence from={imageCarousel.start} durationInFrames={imageCarousel.duration}>
					<ImageCarousel
						primaryColor={primaryColor}
						secondaryColor={secondaryColor}
						durationInFrames={imageCarousel.duration}
						socialImages={data.images ? data.images.filter((img) => img.enabled) : []}
						transitionType={transitionType}
					/>
				</Sequence>
				{/* Text Carousel */}
				<Sequence from={imageCarousel.start} durationInFrames={imageCarousel.duration}>
					<TextCarousel
						primaryColor={primaryColor}
						secondaryColor={secondaryColor}
						durationInFrames={Math.floor(imageCarousel.duration * 0.8)}
						texts={data.texts ? data.texts.filter(text => text && text.content) : []}
						transitionType={transitionType}
					/>
				</Sequence>

				<Sequence from={1 * fps} durationInFrames={imageCarousel.duration}>
					<LogoBlock
						durationInFrames={imageCarousel.duration}
						svgContent={logoSvg || ''}
						styleKey="talk"
					/>
				</Sequence>



				{/*
				<Sequence from={Math.floor(imageCarousel.start * 0.6)} durationInFrames={imageCarousel.duration}>
				<LogoBlock
					durationInFrames={imageCarousel.duration}
					svgContent={logoSvg}
				/>
				</Sequence> */}

				{/* <Sequence  durationInFrames={400}>
			<AbsoluteFill style={{backgroundColor: '#334444'   }}>

				<AnimeJSDemo  durationInFrames={400}/>
				</AbsoluteFill>
			</Sequence> */}


			</AbsoluteFill>
		</div>
	);
};

;
    var _remotion_globalVariableA, _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
